import { Injectable } from '@angular/core';
import { addDoc, collection, collectionData, doc, docData, Firestore, getDoc, getDocs, limit, orderBy, query, startAfter, Timestamp, updateDoc, where } from '@angular/fire/firestore';
import { FormBuilder, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { cloneDeep, isEqual } from 'lodash-es';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProductType } from 'src/app/shared-lib/domains/product';
import { AddressContainerComponent } from '../cart/address-container/address-container.component';
import { AlertModalComponent } from '../components/alert-modal/alert-modal.component';
import { OrderRepeatComponent } from '../components/order-repeat/order-repeat.component';
import { OrderReportIncidentComponent } from '../components/order-report-incident/order-report-incident.component';
import { OutputMessage } from '../models/common/output.model';
import { Order } from '../models/models';
import { OrderDetailComponent } from '../order/order-detail/order-detail.component';
import { OrderPauseDialogComponent } from '../order/order-pause-dialog/order-pause-dialog.component';
import { serverTimestamp } from '../shared-lib/core/db/firestore.models';
import { LoggerService } from '../shared-lib/core/logger';
import { PaperWeight } from '../shared-lib/core/settings';
import { OrderService as DomainOrderService, OrderProcessStatus, UpdatedByFrom, zoneHasChanged } from '../shared-lib/domains/order';
import { UserService } from './../shared-lib/domains/user';
import { AppService } from './app.service';
import { ShareService } from './share.service';

/**
 * Operaciones en base de datos referente a �rdenes
 */
@Injectable({
  providedIn: 'root'
})
export class OrderService {
  public readonly collectionName: string = 'orders';
  public orders: Array<Order>;

  constructor(
    private db: Firestore,
    private domainOrderService: DomainOrderService,
    private userService: UserService,
    private appService: AppService,
    private modalController: ModalController,
    private fb: FormBuilder,
    private shareService: ShareService,
    private alertController: AlertController,
    private logger: LoggerService
  ) {

  }

  ifTimestamp2Date = property => property instanceof Timestamp ? property.toDate() : property;

  get(id): Promise<Order> {
    return getDoc(doc(this.db, `${this.collectionName}/${id}`))
      .then(snap => {
        let ret: Order = null;
        if (snap.exists()) {
          const data = this.normalizeResponse(snap.data());
          ret = { id: snap.id, ...data } as Order;
        }
        return ret;
      });
  }

  get$(id): Observable<Order> {
    return (docData(doc(this.db, `${this.collectionName}/${id}`), { idField: 'id' }) as Observable<Order>)
      .pipe(
        map(order => {
          order = this.normalizeResponse(order);
          return order;
        })
      );
  }

  upsert(_data): Promise<any> {

    const { id, ...data } = _data;
    if (!id) {
      data.createdAt = serverTimestamp();
    }
    data.updatedAt = serverTimestamp();
    data.updatedBy = this.userService.userG?.id ? {
      id: this.userService.userG.id,
      displayName: this.userService.userG.displayName,
      from: UpdatedByFrom.APP
    } : {
      id: null,
      displayName: null,
      from: UpdatedByFrom.APP
    };
    return (id)
      ? updateDoc(doc(this.db, `${this.collectionName}/${id}`), data)
      : addDoc(collection(this.db, this.collectionName), data);
  }

  // devuelve un numero de order asignado por el servidor
  async getOrderNumber(): Promise<any> {
    const { number } = await this.domainOrderService.getOrderBookNumber();
    return number;
  }

  getByUserId(userId, pageSize, startAfterDoc = null) {
    const constraints: any[] = [where('userId', '==', userId), orderBy('createdAt', 'desc'), limit(pageSize)];
    if (startAfterDoc) constraints.push(startAfter(startAfterDoc));
    const q = query(collection(this.db, this.collectionName), ...constraints);
    return collectionData(q, { idField: 'id' }).pipe(
      map(docs => docs.map(a => {
        const data = a as any;
        this.normalizeResponse(data);
        return { ...data };
      }))
    );
  }

  getTotalDraftOrdersCount(userId): Observable<number> {
    const q = query(collection(this.db, `users/${userId}/draftOrders`));
    return from(getDocs(q))
      .pipe(
        map(snapshot => snapshot.size)
      );
  }

  getByUserIdThisMonth(userId) {
    const currentDate = new Date();
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const q = query(
      collection(this.db, this.collectionName),
      where('userId', '==', userId),
      where('createdAt', '>=', firstDay),
      where('createdAt', '<=', lastDay),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    return collectionData(q, { idField: 'id' })
      .pipe(
        map(docs => docs.map(a => {
          const data = a as any;
          this.normalizeResponse(data);
          return { ...data };
        }))
      );
  }

  normalizeResponse(data) {
    data.createdAt = this.ifTimestamp2Date(data.createdAt);
    data.cart.createdAt = this.ifTimestamp2Date(data.cart.createdAt);
    data.cart.updatedAt = this.ifTimestamp2Date(data.cart.updatedAt);
    data = this.back2Present(data);
    return data;
  }

  back2Present(order: Order) {
    order.cart.items.map(item => {
      if (item.product.type === ProductType.PRINT) {
        item.product.printingGroup.printSettings.paperWeight = item.product.printingGroup.printSettings.paperWeight ?? PaperWeight.W80;
      }
      this.appService.upgradePrintProduct(item.product);
      return item;
    });
    return order;
  }

  public onAction(event: OutputMessage): void {
    event.invoke(this);
  }

  async onOpenDetails(data: { order, id, trackingData; }) {
    const { order, id, trackingData } = data;
    const modal = await this.modalController.create({
      component: OrderDetailComponent,
      componentProps: {
        info: { order: order, id: id },
        trackingData: trackingData,
      },
      cssClass: 'order-detail-modal auto-height'
    });

    return await modal.present();
  }

  async onModifyAddress({ order, addressType }) {
    const postalVal = [
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(5),
      Validators.pattern(/^(?:0[1-9]|[1-4]\d|5[0-2])\d{3}$/)
    ];

    // Hay que encontrar cuál es el índice en las direcciones del usuario de los datos usados para este pedido
    const addressesArray = cloneDeep(this.userService.userG?.[`${addressType}Addresses`]);
    const orderAddress = order[`${addressType}Address`];
    const addressIndex = addressesArray?.findIndex((address) => isEqual(address, orderAddress));

    const modal = await this.modalController.create({
      component: AddressContainerComponent, //AddressComponent,
      componentProps: {
        form: this.fb.group({
          recipient: [orderAddress.recipient, [Validators.required, Validators.pattern(/^.{2,}\ .{2,}/)]],
          email: [orderAddress.email, [Validators.email, Validators.required, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,10}$')]],
          addressLine1: [orderAddress.addressLine1, Validators.required],
          street: [orderAddress.street, Validators.required],
          number: [orderAddress.number, Validators.required],
          floor: [orderAddress.floor],
          addressLine2: [orderAddress.addressLine2],
          city: [orderAddress.city, Validators.required],
          province: [orderAddress.province, Validators.required],
          provinceId: [orderAddress.provinceId],
          postalCode: [orderAddress.postalCode, { validators: Validators.compose(postalVal), updateOn: 'change' }],
          country: [orderAddress.country],
          phone: [orderAddress.phone || this.userService.userG?.phoneNumber || order.shippingAddress.phone, [Validators.required, Validators.minLength(9), Validators.maxLength(9), Validators.pattern(/^[0-9]*$/)]],
          organization: [orderAddress.organization],
          taxId: [orderAddress.taxId],
          pickupPointName: [orderAddress.pickupPointName],
          pickupPointCode: [orderAddress.pickupPointCode],
          shippingCourier: [orderAddress.shippingCourier],
          dni: [orderAddress.dni, { updateOn: 'change' }],
        }),
        addressSelected: `${addressType}Address`,
        addresses: addressesArray,
        defaultAddressIndex: addressIndex,
        showClearButton: false,
        orderId: order.id
      },
      cssClass: 'modal-address auto-height',
      id: 'change-address-modal',
      backdropDismiss: false
    });

    modal.onDidDismiss().then(async (resp) => {
      if (resp) {
        const { data } = resp;
        if (data) {
          const { id } = order;

          if (addressType === 'shipping') {
            const newPostalCode = data.shippingAddress.postalCode;
            const oldPostalCode = order.shippingAddress.postalCode;
            if (zoneHasChanged(oldPostalCode, newPostalCode)) {
              const alert = await this.modalController.create({
                component: AlertModalComponent,
                componentProps: {
                  mailToSupport: true
                },
                cssClass: 'auto-height'
              });
              modal.dismiss();
              const processStatus = OrderProcessStatus.ONHOLD;
              await this.upsert({ id, processStatus });
              await alert.present();
              return;
            }
          }

          const address = data[`${addressType}Address`];

          // Si se han realizado acciones sobre los datos de envío o facturación en el modal,
          // se actualizan para el usuario en BD
          let newIndex;
          let updateData = {};
          switch (data.addressAction) {
            // AÑADIR UNA NUEVA DIRECCIÓN
            case 'add':
              addressesArray.push(address);
              updateData[`${addressType}Addresses`] = addressesArray;
              // Si ya existían direcciones, a menos que se marque como por defecto, no se realiza ninguna acción
              // sobre el índice por defecto.
              // Si la dirección añadida es la primera
              if (addressesArray.length === 1) {
                updateData[`default${addressType.charAt(0).toUpperCase() + addressType.slice(1)}AddressIndex`] = 0;
              }
              if (addressesArray.length > 1) {
                newIndex = addressesArray.length - 1;
              }

              break;

            // EDITAR UNA DIRECCIÓN
            case 'edit':
              addressesArray[data.defaultAddressIndex] = address;
              updateData[`${addressType}Addresses`] = addressesArray;
              break;

            default:
              break;
          }

          // Si la nueva dirección se marca como por defecto, se actualiza en el objeto que sobreescribirá la BD
          if (data.setAsDefault) {
            updateData[`default${addressType.charAt(0).toUpperCase() + addressType.slice(1)}AddressIndex`] = newIndex ?? data.defaultAddressIndex;
          }

          const user = this.userService.setUser({ ...this.userService.userG, ...updateData });

          // Se actualizan los datos de envío o facturación en el pedido
          try {
            await user;
            await this.upsert({ id, [`${addressType}Address`]: address, shippingLabelId: null });
          } catch (error) {
            console.error('Error updating order data:', error);
          }
        }
      }
    });

    return await modal.present();
  }

  onShare(orderId) {
    // Compartes la orden seleccionada
    this.shareService.shareOrder(orderId);
  }

  async onPause(order) {

    const modal = await this.modalController.create({
      component: OrderPauseDialogComponent,
      componentProps: { id: order.id },
      backdropDismiss: false
    });

    return await modal.present();
  }

  async onRepeatOrder(order) {
    const modal = await this.modalController.create({
      component: OrderRepeatComponent,
      componentProps: {
        info: { order: order },
      },
      cssClass: 'order-repeat-modal auto-height'
    });

    await modal.present();
  }

  async onReportIncidentOrder(order) {
    if (order.hasReportIncident) return null;

    const modal = await this.modalController.create({
      component: OrderReportIncidentComponent,
      componentProps: {
        info: { order: order },
      },
      cssClass: 'order-report-incident-modal auto-height'
    });

    await modal.present();
  }

  /**
   * Optimizes the order object by removing unnecessary thumbnail data from print product files
   * @param order
   * @returns boolean indicating whether the order size is within acceptable limits after optimization
   * If the order size exceeds the threshold, an alert is shown to the user and the function returns false
   */
  async optimizeAndCheckOrderSize(order): Promise<boolean> {
    let canProceed = true;
    const maxSize = 1 * 1024 * 1024; // 1 MB
    const thresholdPct = 0.85; // 85% del límite
    order.cart.items.forEach(item => {
      if (item.product.type === ProductType.PRINT) {
        const printingGroup = item.product.printingGroup;
        const files = printingGroup.files;
        files.forEach(file => {
          delete file.thumbs;
          delete file.thumbFiles;
        });
      }
    });
    const orderSize = new Blob([JSON.stringify(order)]).size;
    if (orderSize > maxSize * thresholdPct) {
      const alert = await this.alertController.create({
        header: 'Hay un problema con tu pedido',
        message: 'Hemos detectado que tu pedido es demasiado grande. Por favor contacta con Atención al Cliente para resolverlo',
        buttons: ['De acuerdo']
      });
      await alert.present();
      canProceed = false;

      this.logger.warning('Order size exceeds threshold', {
        file: 'order.service.ts',
        func: 'optimizeAndCheckOrderSize',
        type: 'orderSize',
        payload: {
          order: {
            id: order.id,
            number: order.number,
          },
          orderSize,
          clientEmail: order.email ?? null,
          maxSize: maxSize * thresholdPct
        }
      });
    }
    return canProceed;

  }
}
